import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { message, notification } from "antd";
import { technicalAuditService } from "../services/technicalAuditService";
import type {
	IssueFilters,
	IssueSort,
	IssuesListResponse,
	UpdateIssueRequest,
	BulkUpdateRequest,
	BulkUpdateResponse,
	ApplyFixesResponse,
	DuplicateGroupsResponse,
	BulkGenerateSuggestionsResponse,
} from "../types/technicalAudit";

// Query keys
export const ISSUES_QUERY_KEY = ["technical-audit", "issues"] as const;
const RUNS_QUERY_KEY = ["technical-audit", "runs"] as const;

const stickyError = (title: string, description: string) =>
	notification.error({
		message: title,
		description,
		duration: 0,
		placement: "bottomRight",
	});

/**
 * Fetch and cache issues for a specific audit run
 */
export function useTechnicalAuditIssues(
	runId: string,
	_tenantId?: string | null,
	options?: {
		filters?: IssueFilters;
		sort?: IssueSort;
		page?: number;
		pageSize?: number;
		excludeTypes?: string[];
		includeTypes?: string[];
	},
) {
	return useQuery({
		queryKey: [...ISSUES_QUERY_KEY, runId, options],
		queryFn: () => technicalAuditService.getIssues(runId, options),
		enabled: !!runId,
		placeholderData: (previousData) => previousData,
		staleTime: 30_000, // 30 seconds
		// Poll ONLY while background auto-suggestion jobs are in flight so freshly
		// generated suggestions surface without a manual refresh; otherwise stay
		// idle. The backend list endpoint logs at debug() (no INFO flood).
		refetchInterval: (query) =>
			hasInFlightGeneration(query.state.data) ? 5_000 : false,
	});
}

/** True when any issue is queued/generating and has no suggestion yet. */
function hasInFlightGeneration(data: IssuesListResponse | undefined): boolean {
	if (!data?.issues?.length) return false;
	return data.issues.some((issue) => {
		const hasSuggestion = Boolean(
			issue.approved_value || issue.proposed_fix || issue.proposed_value,
		);
		if (hasSuggestion) return false;
		return (
			issue.generation_status === "queued" ||
			issue.generation_status === "generating"
		);
	});
}

/**
 * Update single issue status (legacy status correction or skip)
 */
export function useUpdateIssue() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			issueId,
			data,
		}: {
			issueId: string;
			data: UpdateIssueRequest;
		}) => technicalAuditService.updateIssue(issueId, data),
		onSuccess: (data) => {
			const statusLabel = data.fix_status.replace(/_/g, " ");
			message.success(`Issue status changed to ${statusLabel}`);
			queryClient.invalidateQueries({ queryKey: ISSUES_QUERY_KEY });
		},
		onError: (error: unknown) => {
			const err = error as {
				response?: { data?: { detail?: string } };
				message?: string;
			};
			message.error(
				`Failed to update issue: ${err.response?.data?.detail || err.message || "Unknown error"}`,
			);
		},
	});
}

/**
 * Bulk update issues status
 */
export function useBulkUpdateIssues() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: BulkUpdateRequest) =>
			technicalAuditService.bulkUpdateIssues(data),
		onSuccess: (data: BulkUpdateResponse, variables: BulkUpdateRequest) => {
			if (data.failedCount === 0) {
				message.success(`${data.updatedCount} issues updated successfully`);
			} else {
				notification.warning({
					message: "Bulk update partially failed",
					description: `${data.updatedCount} issues updated, ${data.failedCount} failed: ${(data.errors || []).slice(0, 5).join("; ")}`,
					duration: 0,
					placement: "bottomRight",
				});
			}

			queryClient.invalidateQueries({ queryKey: ISSUES_QUERY_KEY });
			// Approve/skip from the duplicate-groups view: flip the cached group
			// members IMMEDIATELY (Jacob QA: waiting for the refetch left "Apply
			// selected" disabled for seconds after the success toast), then
			// refetch for truth.
			if (variables.action === "approve" && data.failedCount === 0) {
				const approvedIds = new Set(variables.issueIds);
				queryClient.setQueriesData(
					{ queryKey: ["technical-audit", "duplicate-groups"] },
					(old: unknown) => {
						const resp = old as DuplicateGroupsResponse | undefined;
						if (!resp?.groups) return old;
						return {
							...resp,
							groups: resp.groups.map((g) => ({
								...g,
								issues: g.issues.map((i) =>
									approvedIds.has(String(i.id))
										? {
												...i,
												fix_status: "approved" as const,
												approved_value:
													i.approved_value ?? i.proposed_value,
											}
										: i,
								),
							})),
						};
					},
				);
			}
			queryClient.invalidateQueries({
				queryKey: ["technical-audit", "duplicate-groups"],
			});
		},
		onError: (error: unknown) => {
			const err = error as {
				response?: { data?: { detail?: string } };
				message?: string;
			};
			stickyError(
				"Bulk update failed",
				err.response?.data?.detail || err.message || "Unknown error",
			);
		},
	});
}

/**
 * Apply reviewed fixes to WordPress
 */
export function useApplyFixes() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ runId }: { runId: string; tenantId: string | null }) =>
			technicalAuditService.applyFixes(runId),
		onSuccess: (data: ApplyFixesResponse) => {
			const appliedCount = data.applied_count;
			const failedCount = data.failed_count;

				if (failedCount === 0) {
					message.success(
						`${appliedCount} suggested fixes processed successfully`,
					);
				} else {
					message.warning(
						`${appliedCount} suggested fixes processed, ${failedCount} failed. Check details for errors.`,
					);
				}

			queryClient.invalidateQueries({ queryKey: ISSUES_QUERY_KEY });
		},
		onError: (error: unknown) => {
			const err = error as {
				response?: { data?: { detail?: string } };
				message?: string;
			};
			message.error(
				`Failed to apply fixes: ${err.response?.data?.detail || err.message || "Unknown error"}`,
			);
		},
	});
}

/**
 * Apply fix for a single issue with a reviewed suggestion.
 */
export function useApplySingleFix() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			issueId,
			data = {},
		}: {
			issueId: string;
			data?: {
				wp_api_base?: string;
				wp_auth?: unknown;
				approvedValue?: unknown;
				force?: boolean;
				// image_missing_dimensions manual dimension override (both together).
				width?: number;
				height?: number;
				// missing_from_sitemap: explicit confirmation of a site-wide change
				// (un-excluding a whole post type). Set ONLY from the deliberate
				// confirmation checkbox; the backend refuses that apply without it.
				confirm_site_wide?: boolean;
			};
		}) => technicalAuditService.applySingleFix(issueId, data),
		onSuccess: (data) => {
			const result = data.result;
			if (data.success) {
				if (data.result?.method === "wp-content-append") {
					// Phase 1 thin_content blog-only additive apply (append-only).
					const result = data.result as {
						added_sections?: number;
						words_added?: number;
					};
					const sections = Number(result.added_sections ?? 0);
					const words = Number(result.words_added ?? 0);
					message.success(
						`Expansion applied — appended ${sections} section${
							sections === 1 ? "" : "s"
						} (+${words} words). Existing content unchanged.`,
					);
				} else if (data.result?.method === "already_applied") {
					message.info(
						"No change needed — the link already points to the destination.",
					);
				} else if (
					data.fix_status === "recommended" ||
					data.result?.method === "recommendation_only" ||
					(result &&
						"requires_manual_application" in result &&
						result.requires_manual_application)
				) {
					// Recommendation-only: nothing was written to WordPress. Make
					// that explicit so it never reads as a live change.
					message.success(
						data.message ||
							"Recommendation saved — apply it manually in WordPress.",
					);
				} else {
					if (result && "rendered" in result && result.rendered === false) {
						// Saved + SEO plugin active, but the live page has not shown it yet.
						message.warning(
							result.warning ||
								"Applied, but the live page hasn't updated yet (possible cache). Re-check shortly.",
						);
					} else if (result && "rendered" in result && result.rendered === true) {
						// The backend confirms with a CACHE-BUSTED fetch, so the origin is
						// verified — but the public page can keep serving the OLD version
						// for a few minutes behind a page cache/CDN (e.g. WP Engine ignores
						// unknown query strings, so even ?cb= won't bust it). Say so, or a
						// correct apply reads as "didn't apply".
						message.success({
							content:
								"Fix applied and confirmed live on the page. If you still see " +
								"the old value on the site, that's the site's page cache — it " +
								"typically clears within ~10 minutes.",
							duration: 8,
						});
					} else {
						message.success("Fix applied to WordPress");
					}
				}
			} else {
				// Apply did NOT take. Surface the SPECIFIC reason persistently so the
				// user never sees a row that silently stays pending after clicking
				// Apply. The backend returns these flags FLAT on `success: false`
				// (not nested under `result`): `requires_approval`, `skipped` +
				// `excluded_reason`, and `fix_status` ("failed" / passthrough).
				const statusLabel = data.fix_status
					? data.fix_status.replace(/_/g, " ")
					: undefined;

				if (data.fix_status === "obsolete") {
					// NOT a failure: the item was excluded — the page isn't publicly
					// live, or it's a non-editable archive/listing page. Show the
					// friendly backend reason as info (never a red "Apply failed"); the
					// refetch below drops the now-obsolete row from the default view.
					message.info({
						content:
							data.error ||
							"This item was excluded from the audit — it isn't an editable page, so there's nothing to fix here.",
						duration: 8,
						key: "apply-fix-obsolete",
					});
				} else if (data.requires_approval) {
					message.warning({
						content:
							data.error ||
							"This fix needs a reviewed suggested value before it can be applied. Generate or review the suggestion, then Apply again.",
						duration: 8,
							key: "apply-fix-needs-suggestion",
					});
				} else if (data.excluded_reason === "page_builder") {
					// Phase 1 thin_content: the blog post body is page-builder-managed
					// (no editable HTML), so the additive expansion can't be applied.
					// Flag-gated (not error-substring) per the apply contract.
					message.warning({
						content:
							"This post is built with a page builder, so its content can't be auto-expanded. Export the brief and add the sections in the builder.",
						duration: 8,
						key: "apply-fix-page-builder",
					});
				} else if (data.skipped || data.excluded_reason) {
					// No-op: the backend deliberately excluded this URL (e.g. it's a
					// page, and only blog posts are auto-fixed).
					const reason =
						data.excluded_reason === "page"
							? "this URL is a page, and auto-fix is limited to blog posts"
							: data.excluded_reason;
					message.warning({
						content:
							data.error ||
							`Skipped — ${reason || "this issue was excluded from auto-fix"}.`,
						duration: 8,
						key: "apply-fix-skipped",
					});
				} else {
					// A review-only decline (§2.5/§2.6: intentional noindex/canonical,
					// noindex-in-sitemap, etc.) is the safeguard working — not a failure.
					const reason = String(data.error || "Unknown error");
					const isReviewOnly =
						Boolean((data as { review_only?: boolean }).review_only) ||
						/review-only|not auto-applied/i.test(reason);
					// manual_required: an "Apply anyway" override with no safe automatic
					// write (e.g. canonical, or robots meta not REST-writable) — the user
					// fixes it by hand and re-checks. Not a failure.
					const isManualRequired = Boolean(
						(data as { manual_required?: boolean }).manual_required,
					);
					if (isReviewOnly || isManualRequired) {
						message.info(reason);
					} else {
						// Refused / failed / unknown — surface the concrete backend error.
						// Auto-dismiss (antd `message` has no manual close button, so
						// duration:0 leaves it stuck on screen forever). The durable copy
						// + one-click recovery actions live on the issue row/drawer, so the
						// toast only needs to be long enough to read.
						message.error({
							content: `Apply failed: ${reason}${
								statusLabel ? ` (status: ${statusLabel})` : ""
							}`,
							duration: 12,
							key: "apply-fix-failed",
						});
					}
				}
			}
			// Refetch always — even on a no-op/refusal the backend may have moved the
			// row to a terminal status (skipped/failed), so the table must re-read it.
			queryClient.invalidateQueries({ queryKey: ISSUES_QUERY_KEY });
		},
		onError: (error: unknown) => {
			const err = error as {
				response?: { data?: { detail?: string } };
				message?: string;
			};
			message.error(
				`Failed to apply fix: ${err.response?.data?.detail || err.message || "Unknown error"}`,
			);
		},
	});
}

/**
 * Generate an AI suggestion only after the user explicitly requests one.
 */
export function useGenerateIssueSuggestion() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			issueId,
			force = false,
		}: {
			issueId: string;
			force?: boolean;
			}) => technicalAuditService.generateSuggestion(issueId, force),
			onMutate: ({ issueId }) => {
				const messageKey = `technical-audit-generate-suggestion-${issueId}`;
				message.loading({
					content: "Generating suggestion...",
					key: messageKey,
					duration: 0,
				});
				return { messageKey };
			},
			onSuccess: (data, _variables, context) => {
				queryClient.setQueriesData<IssuesListResponse>(
					{ queryKey: ISSUES_QUERY_KEY },
					(old) => {
					if (!old?.issues) return old;
					return {
						...old,
						issues: old.issues.map((issue) =>
							issue.id === data.id
								? {
										...issue,
										...data,
										issue_details: data.issue_details ?? issue.issue_details,
										fix_target: data.fix_target ?? issue.fix_target,
										auto_fix_excluded_reason:
											data.auto_fix_excluded_reason ??
											issue.auto_fix_excluded_reason,
									}
								: issue,
						),
						};
					},
				);
				message.success({
					content: "Suggestion generated",
					key: context?.messageKey,
				});
				queryClient.invalidateQueries({ queryKey: ISSUES_QUERY_KEY });
			},
			onError: (error: unknown, _variables, context) => {
				const err = error as {
					response?: { data?: { detail?: string } };
					message?: string;
				};
				message.error({
					content: `Failed to generate suggestion: ${err.response?.data?.detail || err.message || "Unknown error"}`,
					key: context?.messageKey,
					duration: 6,
				});
			},
		});
}

/**
 * Verify a previously applied fix by re-checking the page
 */
export function useVerifyFix() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (issueId: string) => technicalAuditService.verifyFix(issueId),
		onSuccess: () => {
			// The verified / still-detected / manual-review outcome (incl.
			// before/after + evidence) is surfaced by VerifyResultModal, opened from
			// the caller's onSuccess — no success/warning toast here. Errors still
			// toast below.
			queryClient.invalidateQueries({ queryKey: ISSUES_QUERY_KEY });
		},
		onError: (error: unknown) => {
			const err = error as {
				response?: { data?: { detail?: string } };
				message?: string;
			};
			message.error(
				`Failed to verify fix: ${err.response?.data?.detail || err.message || "Unknown error"}`,
			);
		},
	});
}

/**
 * Enhance audit run with DataForSEO on-page data
 */
export function useDfsEnhance() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (runId: string) => technicalAuditService.dfsEnhance(runId),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ISSUES_QUERY_KEY });
		},
	});
}

/**
 * Fetch and cache audit runs for a tenant
 */
export function useAuditRuns(tenantId: string | null, page = 1, limit = 20) {
	return useQuery({
		queryKey: [...RUNS_QUERY_KEY, tenantId, page, limit],
		queryFn: () => {
			if (!tenantId) throw new Error("Tenant ID is required");
			return technicalAuditService.listRuns(tenantId, page, limit);
		},
		enabled: !!tenantId,
		staleTime: 30_000, // 30 seconds
	});
}

/**
 * Fetch and cache a single audit run by ID
 */
export function useAuditRun(runId: string | null) {
	return useQuery({
		queryKey: [...RUNS_QUERY_KEY, "single", runId],
		queryFn: () => {
			if (!runId) throw new Error("Run ID is required");
			return technicalAuditService.getRun(runId);
		},
		enabled: !!runId,
		staleTime: 30_000,
	});
}

/**
 * Fetch context-aware recovery actions for a FAILED fix apply. Only enabled
 * when an issue id is provided AND the apply has failed — the backend endpoint
 * classifies the failure cause and returns the URLs needed to recover.
 */
export function useRecoveryActions(
	issueId: string | null | undefined,
	isFailed: boolean,
) {
	return useQuery({
		queryKey: [...ISSUES_QUERY_KEY, "recovery-actions", issueId],
		queryFn: () => {
			if (!issueId) throw new Error("Issue ID is required");
			return technicalAuditService.getRecoveryActions(issueId);
		},
		enabled: Boolean(issueId) && isFailed,
		staleTime: 30_000,
	});
}

const TICKETS_QUERY_KEY = ["technical-audit", "tickets"] as const;

/** Cross-workspace tickets — every workspace the user belongs to (Site
 * Health > Tickets). Shares the tickets query-key root, so claim/assign/
 * resolve mutations refresh this view too. */
export function useAllWorkspaceTickets() {
	return useQuery({
		queryKey: [...TICKETS_QUERY_KEY, "all-workspaces"],
		queryFn: () => technicalAuditService.getAllTickets(),
		refetchInterval: 30_000,
	});
}

const BACKLOG_QUERY_KEY = ["technical-audit", "recommendation-backlog"] as const;

/** Cross-workspace recommendation backlog (Tickets > Needs recommendation).
 *  30s polling keeps generation_status badges moving while the drain runs. */
export function useRecommendationBacklog(enabled = true) {
	return useQuery({
		queryKey: [...BACKLOG_QUERY_KEY, "all-workspaces"],
		queryFn: () => technicalAuditService.getRecommendationBacklog(),
		refetchInterval: 30_000,
		enabled,
	});
}

/** Queue backlog issues for background suggestion generation. */
export function useQueueRecommendationBacklog() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (issueIds: string[]) =>
			technicalAuditService.queueRecommendationBacklog(issueIds),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: BACKLOG_QUERY_KEY });
		},
	});
}

/** All manual-fix tickets for a tenant, across every crawl (for the Tickets board). */
export function useManualFixTickets(tenantId: string | null | undefined) {
	return useQuery({
		queryKey: [...TICKETS_QUERY_KEY, tenantId],
		queryFn: () => {
			if (!tenantId) throw new Error("Tenant is required");
			return technicalAuditService.getTickets(tenantId);
		},
		enabled: Boolean(tenantId),
		staleTime: 15_000,
		refetchInterval: 30_000,
	});
}

/** Re-check open tickets and auto-resolve any whose flagged link is already gone. */
export function useReconcileTickets() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (tenantId: string) =>
			technicalAuditService.reconcileTickets(tenantId),
		onSuccess: (res) => {
			if (res.resolved > 0) {
				message.success(
					`Auto-resolved ${res.resolved} ticket${res.resolved === 1 ? "" : "s"} that were already fixed.`,
				);
			} else {
				message.info(`Re-checked ${res.checked} open ticket${res.checked === 1 ? "" : "s"} — none resolved yet.`);
			}
			queryClient.invalidateQueries({ queryKey: TICKETS_QUERY_KEY });
		},
		onError: () => message.error("Couldn't re-check tickets right now."),
	});
}

/** The expert roster for the ticket assign dropdown. */
export function useExperts() {
	return useQuery({
		queryKey: [...TICKETS_QUERY_KEY, "experts"],
		queryFn: () => technicalAuditService.getExperts(),
		staleTime: 5 * 60_000,
	});
}

/** Reassign a ticket to a different expert. */
export function useSetTicketAssignee() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ issueId, expertId }: { issueId: string; expertId: string }) =>
			technicalAuditService.setTicketAssignee(issueId, expertId),
		onSuccess: (res) => {
			message.success(
				res.assignee_name
					? `Assigned to ${res.assignee_name}.`
					: "Ticket unassigned.",
			);
			queryClient.invalidateQueries({ queryKey: TICKETS_QUERY_KEY });
		},
		onError: () => message.error("Couldn't reassign the ticket right now."),
	});
}

/** Move a ticket across the workflow (open / in_progress / resolved). */
export function useSetTicketState() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({
			issueId,
			state,
		}: {
			issueId: string;
			state: "open" | "in_progress" | "resolved";
		}) => technicalAuditService.setTicketState(issueId, state),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: TICKETS_QUERY_KEY });
			queryClient.invalidateQueries({ queryKey: ISSUES_QUERY_KEY });
		},
		onError: () => message.error("Couldn't move the ticket right now."),
	});
}

/** Mark a manual-fix ticket resolved (an expert fixed it by hand). */
export function useResolveTicket() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ issueId, note }: { issueId: string; note?: string }) =>
			technicalAuditService.resolveTicket(issueId, note),
		onSuccess: () => {
			message.success("Ticket marked resolved.");
			queryClient.invalidateQueries({ queryKey: TICKETS_QUERY_KEY });
			queryClient.invalidateQueries({ queryKey: ISSUES_QUERY_KEY });
		},
		onError: () => {
			message.error("Couldn't update the ticket right now — try again shortly.");
		},
	});
}

/**
 * File a manual-fix ticket for an issue that can't be auto-applied. On success the
 * issue moves to the 'manual' (expert fix requested) state and drops from the
 * actionable queue; a ticket is posted to the AgileCo team.
 */
export function useRequestManualFix() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (vars: string | { issueId: string; note?: string }) =>
			typeof vars === "string"
				? technicalAuditService.requestManualFix(vars)
				: technicalAuditService.requestManualFix(vars.issueId, vars.note),
		onSuccess: (res) => {
			message.success(
				res?.message || "Sent to the AgileCo team — an expert will fix this.",
			);
			queryClient.invalidateQueries({ queryKey: ISSUES_QUERY_KEY });
		},
		onError: (error: unknown) => {
			const detail =
				(error as { response?: { data?: { detail?: string } } })?.response?.data
					?.detail ||
				(error as { message?: string })?.message ||
				"Couldn't send the request right now — try again shortly.";
			message.error(detail);
		},
	});
}

/**
 * Client opts in to hands-on help from the Agile & Co team for one issue.
 *
 * Only ever call this from the confirm dialog: it is an opt-in to a separate,
 * human-delivered service, so the client must have read the wording first.
 */
export function useRequestExpertHelp() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (vars: string | { issueId: string; note?: string }) =>
			typeof vars === "string"
				? technicalAuditService.requestExpertHelp(vars)
				: technicalAuditService.requestExpertHelp(vars.issueId, vars.note),
		onSuccess: (res) => {
			message.success(
				res?.message ||
					"Request sent — the Agile & Co team will be in touch about this issue.",
			);
			queryClient.invalidateQueries({ queryKey: ISSUES_QUERY_KEY });
		},
		onError: (error: unknown) => {
			const detail =
				(error as { response?: { data?: { detail?: string } } })?.response?.data
					?.detail ||
				(error as { message?: string })?.message ||
				"Couldn't send the request right now — try again shortly.";
			message.error(detail);
		},
	});
}

/**
 * Self-healing sweep: re-check a run's still-open issues against the live site and
 * close stale ones. Bounded per call; on success it invalidates the issue list so
 * the closed items drop from the (default) queue.
 */
export function useReverifyStale() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (runId: string) => technicalAuditService.reverifyStale(runId),
		onSuccess: (res) => {
			if (res.closed > 0) {
				message.success(
					`Cleaned up ${res.closed} stale issue${res.closed === 1 ? "" : "s"} (checked ${res.checked}).`,
				);
			} else {
				message.info(`No stale issues found (checked ${res.checked}).`);
			}
			queryClient.invalidateQueries({ queryKey: ISSUES_QUERY_KEY });
		},
		onError: () => {
			message.error("Couldn't re-check issues right now — try again shortly.");
		},
	});
}

/**
 * Pre-apply publish-first signal for a source-page content-edit fix. Only enabled
 * when `enabled` is true (caller gates on an eligible, non-terminal issue type +
 * an open drawer/dialog) so non-eligible issues never trigger the live 404 check.
 * A definitive 404/410 on the source page means the page is unpublished — Apply is
 * disabled and the user is prompted to publish it first. Cached (staleTime 60s) so
 * repeated renders of the same open issue don't re-hit the network.
 */
export function useSourcePublishState(
	issueId: string | null | undefined,
	enabled: boolean,
) {
	return useQuery({
		queryKey: [...ISSUES_QUERY_KEY, "source-publish-state", issueId],
		queryFn: () => {
			if (!issueId) throw new Error("Issue ID is required");
			return technicalAuditService.getSourcePublishState(issueId);
		},
		enabled: Boolean(issueId) && enabled,
		staleTime: 60_000,
	});
}

/**
 * Resolve the actions the publish-first chooser can offer (correct WP editor deep
 * link, publishability, in-app editor target). Enabled only when the chooser is
 * shown (source_not_public) so the live WP lookup runs on demand, not per render.
 */
export function usePublishTargets(
	issueId: string | null | undefined,
	enabled: boolean,
) {
	return useQuery({
		queryKey: [...ISSUES_QUERY_KEY, "publish-targets", issueId],
		queryFn: () => {
			if (!issueId) throw new Error("Issue ID is required");
			return technicalAuditService.getPublishTargets(issueId);
		},
		enabled: Boolean(issueId) && enabled,
		staleTime: 60_000,
	});
}

/**
 * Publish an unpublished source page in WordPress (draft -> publish). On success
 * the page is live, so we invalidate the publish-first state + targets (the banner
 * disappears and Apply re-enables) and the issues list.
 */
export function usePublishSource() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (issueId: string) =>
			technicalAuditService.publishSource(issueId),
		onSuccess: (result, issueId) => {
			if (result?.success) {
				message.success(result.message || "Page published in WordPress.");
			} else {
				message.error(
					result?.error || "Couldn't publish the page in WordPress.",
				);
			}
			queryClient.invalidateQueries({
				queryKey: [...ISSUES_QUERY_KEY, "source-publish-state", issueId],
			});
			queryClient.invalidateQueries({
				queryKey: [...ISSUES_QUERY_KEY, "publish-targets", issueId],
			});
			queryClient.invalidateQueries({ queryKey: ISSUES_QUERY_KEY });
		},
		onError: (error: unknown) => {
			const detail =
				(error as { response?: { data?: { detail?: string } } })?.response?.data
					?.detail ||
				(error as { message?: string })?.message ||
				"Couldn't publish the page in WordPress.";
			message.error(detail);
		},
	});
}


// ============================================================================
// Bulk duplicate review (BDD A5)
// ============================================================================

const DUPLICATE_GROUPS_KEY = ["technical-audit", "duplicate-groups"] as const;

/**
 * Consolidated duplicate groups for a run (pages sharing the same
 * title / meta / H1). Refetches on a modest interval while any member is still
 * generating so the group cards update as background suggestions land.
 */
export function useDuplicateGroups(runId: string | null) {
	return useQuery({
		queryKey: [...DUPLICATE_GROUPS_KEY, runId],
		queryFn: () => technicalAuditService.getDuplicateGroups(runId!),
		enabled: !!runId,
		// This response is ~900KB and takes seconds to build (58 groups / 241
		// decorated issues). Polling it faster than it can answer just queues
		// overlapping requests. staleTime dedupes the manual invalidations the
		// panel fires while a batch is in flight.
		staleTime: 8_000,
		refetchInterval: (query) => {
			const data = query.state.data as DuplicateGroupsResponse | undefined;
			const generating = data?.groups?.some((g) =>
				g.issues.some(
					(i) =>
						i.generation_status === "queued" ||
						i.generation_status === "generating",
				),
			);
			return generating ? 10_000 : false;
		},
	});
}

/** Bulk-queue suggestion generation for a duplicate group's members. */
export function useBulkGenerateSuggestions() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (vars: {
			issueIds: string[];
			force?: boolean;
			includeApproved?: boolean;
		}) =>
			technicalAuditService.bulkGenerateSuggestions(
				vars.issueIds,
				vars.force,
				vars.includeApproved,
			),
		onSuccess: (data: BulkGenerateSuggestionsResponse) => {
			if (data.queued > 0) {
				message.success(
					`Generating suggestions for ${data.queued} ${data.queued === 1 ? "page" : "pages"} in the background…`,
				);
			} else {
				message.info(
					"Nothing to generate — the selected pages already have suggestions or are done.",
				);
			}
			queryClient.invalidateQueries({ queryKey: DUPLICATE_GROUPS_KEY });
			queryClient.invalidateQueries({ queryKey: ISSUES_QUERY_KEY });
		},
		onError: (error: unknown) => {
			const err = error as {
				response?: { data?: { detail?: string } };
				message?: string;
			};
			stickyError(
				"Failed to queue suggestion generation",
				err.response?.data?.detail || err.message || "Unknown error",
			);
		},
	});
}

/** "Apply selected" — batch-apply only the given APPROVED issues (A5). */
export function useApplySelectedFixes() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ runId, issueIds }: { runId: string; issueIds: string[] }) =>
			technicalAuditService.applySelectedFixes(runId, issueIds),
		onSuccess: (
			data: ApplyFixesResponse,
			variables: { runId: string; issueIds: string[] },
		) => {
			const applied = data.applied_count;
			const failed = data.failed_count;
			// Flip the groups cache immediately on a fully-successful apply so the
			// group's "N/M applied" badge updates the moment the modal says done,
			// rather than after the refetch round-trip (Jacob QA).
			if (failed === 0) {
				const targets = new Set(variables.issueIds.map(String));
				queryClient.setQueriesData<DuplicateGroupsResponse>(
					{ queryKey: DUPLICATE_GROUPS_KEY },
					(old) =>
						old
							? {
									...old,
									groups: old.groups.map((g) => ({
										...g,
										issues: g.issues.map((i) =>
											targets.has(String(i.id))
												? { ...i, fix_status: "applied" }
												: i,
										),
									})),
								}
							: old,
				);
			}
			if (failed === 0) {
				message.success(
					`${applied} ${applied === 1 ? "fix" : "fixes"} applied to WordPress`,
				);
			} else {
				message.warning(
					`${applied} applied, ${failed} failed — open the issues for details.`,
				);
			}
			queryClient.invalidateQueries({ queryKey: DUPLICATE_GROUPS_KEY });
			queryClient.invalidateQueries({ queryKey: ISSUES_QUERY_KEY });
		},
		onError: (error: unknown) => {
			const err = error as {
				response?: { data?: { detail?: string } };
				message?: string;
			};
			stickyError(
				"Apply selected failed",
				err.response?.data?.detail || err.message || "Unknown error",
			);
		},
	});
}
